import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, formatApiError } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ORG_LABEL, STATO_BADGE, fmtDateTime } from "@/lib/constants";
import { PmBot } from "@/components/PmBot";

const Kpi = ({ label, valore, nota, testid, accento = "text-slate-900" }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid={testid}>
    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
    <p className={`mt-2 text-3xl font-bold tabular-nums ${accento}`}>{valore}</p>
    {nota && <p className="mt-1 text-xs text-slate-500">{nota}</p>}
  </div>
);

export default function Qualita() {
  const { projectId } = useProject();
  const [dati, setDati] = useState(null);
  const [sprint, setSprint] = useState("all");
  const [sprints, setSprints] = useState([]);

  const carica = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/qualita`, {
        params: sprint === "all" ? {} : { sprint },
      });
      setDati(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [projectId, sprint]);

  useEffect(() => {
    carica();
  }, [carica]);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/plan`).then(({ data }) => setSprints(data.sprints || []));
  }, [projectId]);

  if (!dati)
    return (
      <div className="grid h-64 place-items-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  const k = dati.kpi;

  return (
    <div className="space-y-7 sm:space-y-9" data-testid="qualita-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-600">
            <ShieldCheck className="h-3.5 w-3.5" /> Qualità &amp; Test
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Esiti di test e rework
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-500 md:text-base">
            Quanti KO ha dato ogni tester, su quali task si ripetono e come sta andando la qualità nel tempo.
          </p>
        </div>
        <Select value={sprint} onValueChange={setSprint}>
          <SelectTrigger className="h-10 w-full lg:w-56" data-testid="qualita-sprint-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Tutti gli sprint</SelectItem>
            {sprints.map((s) => (
              <SelectItem key={s.key} value={s.key}>{s.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
        <Kpi label="Test eseguiti" valore={k.test_totali} testid="kpi-test-totali" />
        <Kpi label="Esiti KO" valore={k.ko_totali} accento="text-rose-600" testid="kpi-ko-totali" />
        <Kpi label="Esiti OK" valore={k.ok_totali} accento="text-emerald-600" testid="kpi-ok-totali" />
        <Kpi label="Tasso KO" valore={`${k.tasso_ko}%`} accento={k.tasso_ko > 40 ? "text-rose-600" : "text-slate-900"} testid="kpi-tasso-ko" />
        <Kpi label="Rework totali" valore={k.rework_totale} nota={`${k.task_con_rework} task coinvolti`} testid="kpi-rework" />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            KO per tester
          </p>
          {dati.classifica_tester.length === 0 ? (
            <p className="mt-6 text-sm text-slate-400" data-testid="tester-empty">
              Nessun esito di test registrato per questo filtro.
            </p>
          ) : (
            <div className="mt-4 space-y-3" data-testid="classifica-tester">
              {dati.classifica_tester.map((t) => (
                <div key={t.tester} className="rounded-xl border border-slate-100 bg-slate-50/70 p-3.5"
                     data-testid={`tester-row-${t.tester.replace(/\s+/g, "-")}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">{t.tester}</p>
                    <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${t.tasso_ko > 40 ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
                      {t.tasso_ko}% KO
                    </span>
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-500">
                    <span className="flex items-center gap-1.5 text-rose-600">
                      <XCircle className="h-3.5 w-3.5" /> {t.ko} KO
                    </span>
                    <span className="flex items-center gap-1.5 text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {t.ok} OK
                    </span>
                    <span>su {t.task} task diversi</span>
                  </div>
                  <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full bg-rose-500 transition-all duration-500" style={{ width: `${t.tasso_ko}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Andamento esiti nel tempo
          </p>
          {dati.andamento.length === 0 ? (
            <p className="mt-6 text-sm text-slate-400" data-testid="andamento-empty">
              Servono almeno alcuni test per disegnare l'andamento.
            </p>
          ) : (
            <div className="mt-4 h-60" data-testid="andamento-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dati.andamento}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="data" tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="ok" name="OK" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="ko" name="KO" fill="#F43F5E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
          Task con KO ricorrenti
        </p>
        {dati.task_ricorrenti.length === 0 ? (
          <p className="mt-6 text-sm text-slate-400" data-testid="ricorrenti-empty">
            Nessun task ha ancora ricevuto un KO.
          </p>
        ) : (
          <div className="mt-4 space-y-2.5" data-testid="task-ricorrenti">
            {dati.task_ricorrenti.map((t) => (
              <div key={t.codice}
                   className="flex flex-col gap-2 rounded-xl border border-slate-100 p-3.5 sm:flex-row sm:items-center sm:justify-between"
                   data-testid={`ricorrente-${t.codice}`}>
                <div className="min-w-0">
                  <p className="font-mono text-[11px] text-slate-400">{t.codice}</p>
                  <p className="text-sm font-medium text-slate-900">{t.task}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Tester: {t.tester.join(", ")} · ultimo KO {fmtDateTime(t.ultimo_ko)}
                  </p>
                </div>
                <span className={`shrink-0 self-start rounded-full px-2.5 py-1 font-mono text-[11px] font-bold sm:self-center ${t.ko >= 3 ? "bg-rose-100 text-rose-700" : "bg-amber-50 text-amber-700"}`}>
                  {t.ko} KO
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Task in attesa di validazione
          </p>
          {dati.in_test.length === 0 ? (
            <p className="mt-6 text-sm text-slate-400" data-testid="in-test-empty">
              Nessun task è attualmente in test.
            </p>
          ) : (
            <div className="mt-4 space-y-2" data-testid="in-test-list">
              {dati.in_test.map((t) => (
                <div key={t.codice} className="rounded-lg bg-amber-50/70 px-3 py-2" data-testid={`in-test-${t.codice}`}>
                  <p className="font-mono text-[11px] text-amber-700">{t.codice} · {t.sprint}</p>
                  <p className="text-sm text-slate-800">{t.task}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Rework aperti per esecutore
          </p>
          {dati.rework_aperti.length === 0 ? (
            <p className="mt-6 text-sm text-slate-400" data-testid="rework-empty">
              Nessun rework registrato.
            </p>
          ) : (
            <div className="mt-4 space-y-2.5" data-testid="rework-list">
              {dati.rework_aperti.map((t) => (
                <div key={t.codice} className="rounded-xl border border-slate-100 p-3.5" data-testid={`rework-${t.codice}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-mono text-[11px] text-slate-400">{t.codice}</p>
                    <div className="flex items-center gap-2">
                      <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${STATO_BADGE[t.stato] || "bg-slate-100 text-slate-600"}`}>
                        {t.stato}
                      </span>
                      <span className="flex items-center gap-1 font-mono text-[11px] font-bold text-rose-600">
                        <AlertTriangle className="h-3 w-3" /> {t.rework}
                      </span>
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-slate-800">{t.task}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {ORG_LABEL[t.org] || t.org} · {t.assegnatario || "non assegnato"}
                  </p>
                  {t.note_test && (
                    <p className="mt-2 rounded-lg bg-rose-50/70 px-2.5 py-1.5 text-xs italic text-rose-800">
                      “{t.note_test}” — {t.testato_da}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <PmBot pagina="test" sprint={sprint === "all" ? null : sprint} />
    </div>
  );
}
